import React, { MouseEvent, MouseEventHandler, useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { ConnectableElement, useDrop } from 'react-dnd'

import { IBlock } from 'common/builder/blocks/blockInterfaces'
import { IButton } from 'common/builder/buttons/buttonInterfaces'
import { BlockType } from 'common/builder/constants/BlockType'
import { ButtonStyle } from 'common/builder/constants/ButtonStyle'
import { ButtonType } from 'common/builder/constants/ButtonType'
import ButtonModel from 'common/builder/models/Button'
import { INode } from 'common/builder/nodes/nodeInterfaces'
import SortableGroups from 'constants/SortableGroups'

import { ButtonContent } from './Components/ButtonContent'
import { PopoverContent } from './Components/PopoverContent'

import cm from './Button.module.css'

interface ButtonProps {
  button: IButton
  buttonBlock: IBlock
  readonly: boolean
  content: INode
  isSimple: boolean
  onOpenNode: MouseEventHandler<SVGElement>
  onSelect: () => void
  onDeselect: () => void
  selected: boolean
  errors: Array<{ prop: string }>
  showErrors: boolean
  serverError: boolean
  rounded: boolean
  mini: boolean
  className: string
  onMouseEnter: () => void
  onMouseLeave: () => void
  first: boolean
  disableHover: boolean
  isDragging: boolean
  disabled: boolean
  dragHolder: (element: ConnectableElement) => ConnectableElement
  hasTarget: boolean
  btnRefClassName: string
  builderId: string
  isFallback: boolean
  onRemove: () => void
  hideSaveButton: boolean
  onDrop: (content: INode) => void
}

export const Button = ({
  button,
  buttonBlock,
  readonly,
  content,
  isSimple,
  onOpenNode,
  onSelect,
  onDeselect,
  selected,
  errors,
  showErrors,
  serverError,
  rounded,
  mini,
  className,
  onMouseEnter,
  onMouseLeave,
  first,
  disableHover,
  isDragging,
  disabled,
  dragHolder,
  hasTarget,
  btnRefClassName,
  builderId,
  isFallback,
  onRemove,
  hideSaveButton,
  onDrop,
}: ButtonProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [dirty, setDirty] = useState(false)
  const prevSelected = useRef(selected)

  const [{ isOver, canDrop }, drop] = useDrop<
    { content: INode },
    void,
    { isOver: boolean; canDrop: boolean }
  >({
    accept: SortableGroups.CONTENT_ITEM_LIST,
    drop: (item) => {
      if (button.type === null || button.type === ButtonType.CONTENT) {
        onDrop(item.content)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  useEffect(() => {
    if (prevSelected.current && !selected) {
      setDirty(false)
    }
    prevSelected.current = selected
  }, [selected])

  useEffect(() => {
    setDirty(false)
  }, [button.type])

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    // Stopping event to prevent block selection
    event.stopPropagation()
    toggleSelect()
  }

  const toggleSelect = () => {
    if (selected) {
      return onDeselect()
    }
    onSelect()
  }

  const captionError =
    errors.find((err) => err.prop === 'caption' || err.prop === 'description') &&
    (showErrors || dirty)

  const cn = cx('p-a-xs _cp', cm.btn, className, cm[button.type as keyof typeof cm], {
    [cm.rounded]: rounded,
    [cm.solid]: buttonBlock.buttonsStyle === ButtonStyle.SOLID,
    [cm.text]: buttonBlock.buttonsStyle === ButtonStyle.TEXT,
    [cm.selected]: selected,
    [cm.error]: (errors.length > 0 && showErrors) || serverError,
    [cm.captionError]: captionError,
    [cm.mini]: mini,
    [cm.first]: first,
    [cm.dropTarget]:
      (button.type == null || [ButtonType.CONTENT, ButtonType.BUY].includes(button.type)) &&
      canDrop,
    [cm.over]: isOver,
    [cm.dragging]: isDragging,
    [cm.disableHover]: disableHover,
    [cm.readonly]: readonly,
  })

  const buttonIsNotDraggable = [
    BlockType.ONE_TIME_NOTIFY_REQUEST,
    BlockType.MESSAGE_TEMPLATE,
  ].includes(buttonBlock?.type)

  const stats = ButtonModel.getStats(button, content)

  drop(rootRef)
  return (
    <>
      <div
        key="main"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={cn}
        onClick={handleClick}
        ref={rootRef}
      >
        <div data-id={button.id}>
          <ButtonContent
            button={button}
            buttonBlock={buttonBlock}
            readonly={readonly}
            stats={stats}
            isSimple={isSimple}
            disabled={disabled}
            dragHolder={dragHolder}
            hasTarget={hasTarget}
            errors={errors}
            showErrors={showErrors}
            btnRefClassName={btnRefClassName}
            onOpenNode={onOpenNode}
            dirty={dirty}
          />
        </div>
      </div>

      <PopoverContent
        builderId={builderId}
        isFallback={isFallback}
        onDeselect={onDeselect}
        anchor={rootRef.current}
        readonly={readonly}
        button={button}
        selected={selected}
        errors={errors}
        showErrors={showErrors}
        buttonIsNotDraggable={buttonIsNotDraggable}
        dirty={dirty}
        onRemove={onRemove}
        hideSaveButton={hideSaveButton}
      />
    </>
  )
}
